import { createFormHook, formOptions } from "@tanstack/react-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import type { Announcement, AnnouncementIcon } from "@dashboard/api/task";
import { fieldContext, formContext } from "./Form";
import { IconSelect, Input, TextArea } from "./FormComponents";
import { announcementIconKeys } from "@dashboard/features/Dashboard/Announcements/announcementIconMap";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { useCreateAnnouncement, useUpdateAnnouncement } from "@dashboard/hooks/useAnnouncements";

export const VALIDATION_LIMITS_ANNOUNCEMENT = {
    TITLE_MAX: 100,
    CONTENT_MAX: 500,
} as const;

export type AnnouncementFormValues = {
    title: string;
    content: string;
    icon: AnnouncementIcon;
};

const { useAppForm: useAnnouncementForm } = createFormHook({
    fieldContext,
    formContext,
    fieldComponents: { Input, TextArea, IconSelect },
    formComponents: {},
});

export const useAnnouncementFormSetup = () => {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const createMutation = useCreateAnnouncement();
    const updateMutation = useUpdateAnnouncement();

    const formSchema = z.object({
        title: z
            .string()
            .trim()
            .min(1, t("common.titleIsRequired"))
            .max(VALIDATION_LIMITS_ANNOUNCEMENT.TITLE_MAX, t("common.titleMaxLength")),
        content: z
            .string()
            .max(VALIDATION_LIMITS_ANNOUNCEMENT.CONTENT_MAX, t("common.descriptionMaxLength")),
        icon: z.enum(announcementIconKeys),
    });

    const mapAnnouncementToForm = (announcement: Announcement): AnnouncementFormValues => ({
        title: announcement.title ?? "",
        content: announcement.content ?? "",
        icon: announcement.icon ?? "ANNOUNCEMENT",
    });

    const handleAnnouncementSubmit = async (
        value: AnnouncementFormValues,
        announcementId?: number,
    ) => {
        try {
            if (announcementId) {
                await updateMutation.mutateAsync({
                    id: announcementId,
                    data: {
                        title: value.title.trim(),
                        content: value.content.trim() || null,
                        icon: value.icon,
                    },
                });
            } else {
                await createMutation.mutateAsync({
                    instanceId,
                    title: value.title.trim(),
                    content: value.content.trim() || null,
                    icon: value.icon,
                });
            }
            return { success: true as const };
        } catch (error) {
            console.error("Failed to save announcement:", error);
            return { success: false as const, error };
        }
    };

    const formOpts = formOptions({
        defaultValues: {
            title: "",
            content: "",
            icon: "ANNOUNCEMENT",
        } as AnnouncementFormValues,
        validators: {
            onSubmit: formSchema,
        },
    });

    return {
        useAnnouncementForm,
        formOpts,
        mapAnnouncementToForm,
        handleAnnouncementSubmit,
    };
};
